// mission-known-secret-values-registry.ts — phase 04a (spec V2 §4.2, B17/B21/N28).
//
// MỘT nguồn known-values cho mọi sink của controller (journal qua
// `MissionStore.commit`, forensics, retry evidence, stream worker, interview,
// status, mô tả MR). Thuần bộ nhớ, cấp process:
//  - khoá theo `mission_id`; KHÔNG serialize, KHÔNG ghi đĩa (giá trị stdin của
//    phase 04b không bao giờ chạm file nào — §4.2 (11));
//  - CHỈ THÊM, không bao giờ co trong vòng đời process (B21): tách nguồn
//    `test_env` / `stdin`, nạp lại = hợp ⇒ nạp lại `test.env` trước deploy không
//    đẩy mất giá trị stdin, giá trị cũ (trước khi sửa file) vẫn bị che;
//  - chỉ giữ giá trị ≥ 8 ký tự (§4.2, `registrableKnownSecretValues`);
//  - Codex F8: cũng giữ TÊN key đã thấy (stdin + `test.env` nạp `ok`) — token
//    ranh giới của luật auto (§4.1 điều 3), chỉ thêm như giá trị.
//
// Getter có stat `test.env` (B20) nằm ở mission-secrets-test-env-loader.ts
// (`knownValuesFor`) — đặt ở đây sẽ thành vòng import loader ⇄ registry.
import { registrableKnownSecretValues } from "./known-secret-value-encoded-variants.js";
import { redactionFormsOfTestEnvValues } from "./mission-secrets-test-env-parser-and-permissions.js";

export type KnownSecretSource = "test_env" | "stdin";

interface MissionEntry {
  bySource: Record<KnownSecretSource, Set<string>>;
  keyNames: Set<string>;
  /** Chữ ký stat lần cuối của `test.env` (getter so để biết có cần nạp lại). */
  testEnvSignature?: string;
}

const entries = new Map<string, MissionEntry>();

function entryFor(missionId: string): MissionEntry {
  let entry = entries.get(missionId);
  if (entry === undefined) {
    entry = { bySource: { test_env: new Set(), stdin: new Set() }, keyNames: new Set() };
    entries.set(missionId, entry);
  }
  return entry;
}

/** Hợp giá trị (≥ 8 ký tự) vào nguồn `source` của mission. Không bao giờ xoá. */
export function addKnownSecretValues(
  missionId: string,
  source: KnownSecretSource,
  values: Iterable<string>,
): void {
  const kept = registrableKnownSecretValues(values);
  if (kept.length === 0) return;
  const set = entryFor(missionId).bySource[source];
  for (const value of kept) set.add(value);
}

/**
 * Phase 04b (`run --release-secrets-stdin` + merger env release): cùng dạng che
 * như `test.env` (nguyên văn + phần trong cặp nháy bao ngoài, review 04a L9).
 */
export function addStdinValues(missionId: string, map: Record<string, string>): void {
  addKnownSecretValues(missionId, "stdin", redactionFormsOfTestEnvValues(map));
  addKnownSecretKeyNames(missionId, Object.keys(map));
}

/** Codex F8: hợp tên key của nguồn secrets (không bao giờ xoá trong process). */
export function addKnownSecretKeyNames(missionId: string, keys: Iterable<string>): void {
  const set = entryFor(missionId).keyNames;
  for (const key of keys) set.add(key);
}

/** Mọi tên key secrets đã thấy của mission trong process này. */
export function registeredSecretKeyNames(missionId: string): string[] {
  return [...(entries.get(missionId)?.keyNames ?? [])];
}

/** Hợp mọi nguồn của mission — rỗng khi mission chưa có secret nào. */
export function registeredKnownValues(missionId: string): string[] {
  const entry = entries.get(missionId);
  if (entry === undefined) return [];
  return [...new Set([...entry.bySource.test_env, ...entry.bySource.stdin])];
}

export function testEnvSignatureFor(missionId: string): string | undefined {
  return entries.get(missionId)?.testEnvSignature;
}

export function recordTestEnvSignature(missionId: string, signature: string): void {
  entryFor(missionId).testEnvSignature = signature;
}

/** N28: chỉ test gọi — process thật không bao giờ co registry. */
export function resetForTests(): void {
  entries.clear();
}
